import { useRef } from 'react';
import { Grid, GridItem, Button, Icon, Text, Menu, Portal } from '@chakra-ui/react';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { isEmpty } from 'lodash';
import useContentState from './use-content-state';
import { Author, FieldOption } from '@/lib/types';

interface FilterConfig {
  label: string;
  id: string;
  key: string;
  options: FieldOption[];
}

interface Props {
  filterConfig: FilterConfig[];
  optionConfig: {
    [key: string]: {
      [key: string]: number;
    };
  };
  authors?: Author[];
  filters: any[];
  setFilters: (filters: any[]) => void;
  onFilterChange: (filters: { [key: string]: string[] }) => void;
  onResetFilters: () => void;
}

const Filters = ({
  filterConfig,
  optionConfig,
  authors,
  filters,
  setFilters,
  onFilterChange,
  onResetFilters,
}: Props) => {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const { hasAuthor, setHasAuthor, selectedFilters, setSelectedFilters, clearSelectedFilters } =
    useContentState();

  function getSelectedLabels(key: string) {
    const selectedValues = selectedFilters[key] || [];

    const selectionItem = filterConfig.find((item) => item.key === key);
    if (!selectionItem) return '';
    if (selectedValues.length === 0) return '';

    const selectedLabels = selectionItem.options
      .filter((option) => selectedValues.includes(option.option_value))
      .map((option) => option.option_label);

    if (selectedLabels.length > 1) {
      return (
        <>
          {selectedLabels[0]}{' '}
          <span style={{ color: 'gray', fontSize: '10px', fontWeight: 'normal' }}>
            (+{selectedLabels.length - 1} more)
          </span>
        </>
      );
    }

    return selectedLabels.join(', ');
  }

  const handleFilterChange = (key: string, value: string[]) => {
    const newFilters = { ...selectedFilters, [key]: value };
    setSelectedFilters(newFilters);
    onFilterChange(newFilters);
  };

  function handleResetFilters() {
    setFilters([{ key: 'status', val: 'published' }]);
    setHasAuthor(false);
    onFilterChange({});
    clearSelectedFilters();
    onResetFilters();
  }

  function changeAuthorFilter(val: string) {
    setFilters([
      { key: 'created_by', val: val },
      { key: 'status', val: 'published' },
    ]);
    setHasAuthor(true);
    clearSelectedFilters();
    onResetFilters();
  }

  return (
    <Grid
      templateColumns={{
        base: 'repeat(2, 1fr)',
        md: 'repeat(3, 1fr)',
        lg: 'repeat(5, 1fr)',
        xl: 'repeat(6, 1fr)',
      }}
      gap={3}
      ref={contentRef}
    >
      {authors && authors.length > 0 && (
        <GridItem w='100%'>
          <Menu.Root>
            <Menu.Trigger asChild>
              <Button
                variant={'outline'}
                size={'sm'}
                w={'100%'}
                textAlign={'left'}
                justifyContent={'flex-start'}
              >
                <Icon as={ChevronDownIcon} w={4} h={4} />
                {filters.find((filter) => filter.key === 'created_by')
                  ? authors.find(
                      (author) =>
                        author.user_id.toString() ===
                        filters.find((filter) => filter.key === 'created_by')?.val,
                    )?.first_name +
                    ' ' +
                    authors.find(
                      (author) =>
                        author.user_id.toString() ===
                        filters.find((filter) => filter.key === 'created_by')?.val,
                    )?.last_name
                  : 'Author'}
              </Button>
            </Menu.Trigger>
            <Portal container={contentRef}>
              <Menu.Positioner>
                <Menu.Content>
                  <Menu.ItemGroup>
                    {authors.map((author) => (
                      <Menu.RadioItemGroup
                        key={author.user_id}
                        value={filters.find((filter) => filter.key === 'created_by')?.val || ''}
                        onValueChange={(e) => {
                          changeAuthorFilter(e.value);
                        }}
                      >
                        <Menu.RadioItem value={author.user_id.toString()}>
                          {author.first_name + ' ' + author.last_name}
                        </Menu.RadioItem>
                      </Menu.RadioItemGroup>
                    ))}
                  </Menu.ItemGroup>
                </Menu.Content>
              </Menu.Positioner>
            </Portal>
          </Menu.Root>
        </GridItem>
      )}
      {filterConfig.map(
        (filter) =>
          optionConfig &&
          !isEmpty(optionConfig[filter.key]) && (
            <GridItem w='100%' key={filter.id}>
              <Menu.Root>
                <Menu.Trigger asChild>
                  <Button
                    variant={'outline'}
                    size={'sm'}
                    w={'100%'}
                    textAlign={'left'}
                    justifyContent={'flex-start'}
                  >
                    <Icon as={ChevronDownIcon} w={4} h={4} />
                    {getSelectedLabels(filter.key) || filter.label}
                  </Button>
                </Menu.Trigger>
                <Portal container={contentRef}>
                  <Menu.Positioner>
                    <Menu.Content>
                      <Menu.ItemGroup>
                        {filter.options.map(
                          (option) =>
                            optionConfig[filter.key] &&
                            optionConfig[filter.key][option.option_value] && (
                              <Menu.CheckboxItem
                                key={option.id}
                                value={option.option_value}
                                checked={selectedFilters[filter.key]?.includes(option.option_value)}
                                onCheckedChange={(checked) => {
                                  const currentValues = selectedFilters[filter.key] || [];
                                  const newValues = checked
                                    ? [...currentValues, option.option_value]
                                    : currentValues.filter((v) => v !== option.option_value);
                                  handleFilterChange(filter.key, newValues);
                                }}
                              >
                                {option.option_label}
                                {optionConfig[filter.key][option.option_value] && (
                                  <Text as={'b'} fontWeight={'medium'} ml={1}>
                                    ({optionConfig[filter.key][option.option_value]})
                                  </Text>
                                )}
                              </Menu.CheckboxItem>
                            ),
                        )}
                      </Menu.ItemGroup>
                    </Menu.Content>
                  </Menu.Positioner>
                </Portal>
              </Menu.Root>
            </GridItem>
          ),
      )}
      {hasAuthor || (selectedFilters && Object.keys(selectedFilters).length) ? (
        <Button
          aria-label={'Reset filters'}
          variant={'ghost'}
          size={'sm'}
          w={'90px'}
          onClick={handleResetFilters}
        >
          Reset
          <Icon as={XMarkIcon} w={4} h={4} />
        </Button>
      ) : null}
    </Grid>
  );
};

export default Filters;
